import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import BackupCard from '../components/Backup/BackupCard';
import BackupConfigModal from '../components/Backup/BackupConfigModal';
import RestoreHistoryCard from '../components/Backup/RestoreHistoryCard';
import apiService from '../services/api';
import { Backup, BackupConfiguration, RestoreOperation } from '../types/api';
import { darkModeClasses } from '../utils/darkModeClasses';
import { getApiErrorMessage } from '../constants/messages';
import Logger from '../utils/logger';

const BackupManagementPage: React.FC = () => {
  const { t } = (useTranslation as any)('backups');
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const navigate = useNavigate();

  const [backups, setBackups] = useState<Backup[]>([]);
  const [configuration, setConfiguration] = useState<BackupConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(10);
  const [creating, setCreating] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [deploymentName, setDeploymentName] = useState<string>('');
  const [activeRestore, setActiveRestore] = useState<RestoreOperation | null>(null);
  const [restoreHistory, setRestoreHistory] = useState<RestoreOperation[]>([]);

  useEffect(() => {
    if (deploymentId) {
      loadData();
    }
  }, [deploymentId, page]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load deployment details
      try {
        const details = await apiService.getDeploymentDetails(deploymentId!);
        setDeploymentName(details.application_name);
      } catch (error) {
        Logger.error('Error loading deployment details:', error);
      }

      // Load backup configuration
      try {
        const config = await apiService.getBackupConfiguration(deploymentId!);
        setConfiguration(config);
      } catch (error: unknown) {
        const apiError = error as { response?: { status?: number } };
        if (apiError.response?.status !== 404) {
          Logger.error('Error loading backup configuration:', error);
        }
        // 404 is OK - means no configuration yet
      }

      // Load backups list
      const response = await apiService.listBackups(deploymentId!, page, pageSize);
      setBackups(response.backups);
      setTotal(response.total);

      // Load restore operations to check if there's an active one
      try {
        const restoresResponse = await apiService.listRestores(deploymentId!);
        Logger.log('Restores loaded:', restoresResponse);

        // Store all restore operations for history
        setRestoreHistory(restoresResponse.restore_operations || []);

        // Find an active restore (pending, in_progress, or awaiting_validation)
        const activeRestoreOp = restoresResponse.restore_operations?.find(
          (restore) =>
            restore.status === 'pending' ||
            restore.status === 'in_progress' ||
            restore.status === 'awaiting_validation'
        );
        setActiveRestore(activeRestoreOp || null);
      } catch (error: unknown) {
        Logger.error('Error loading restore operations:', error);
        setRestoreHistory([]);
        // Non-blocking error, just log it
      }
    } catch (error) {
      Logger.error('Error loading backup data:', error);
      toast.error(t('management.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      await apiService.createOnDemandBackup(deploymentId!);
      toast.success(t('management.manualBackupSuccess'));
      // Reload backups after a short delay
      setTimeout(() => loadData(), 2000);
    } catch (error: unknown) {
      Logger.error('Error creating backup:', error);
      toast.error(getApiErrorMessage(error) || t('management.manualBackupError'));
    } finally {
      setCreating(false);
    }
  };

  const handleRestore = (backupId: string) => {
    navigate(`/deployments/${deploymentId}/backups/restore/${backupId}`);
  };

  const handleDelete = async (backupId: string) => {
    if (!window.confirm(t('management.deleteConfirm') as string)) {
      return;
    }

    try {
      await apiService.deleteBackup(deploymentId!, backupId);
      toast.success(t('management.deleteSuccess'));
      loadData(); // Recharger la liste
    } catch (error: unknown) {
      Logger.error('Error deleting backup:', error);
      toast.error(getApiErrorMessage(error) || t('management.deleteError'));
    }
  };

  const handleDeleteRestore = async (restoreId: string) => {
    if (!window.confirm(t('management.deleteRestoreConfirm') as string)) {
      return;
    }

    try {
      await apiService.deleteRestore(deploymentId!, restoreId);
      toast.success(t('management.deleteRestoreSuccess'));
      loadData(); // Recharger la liste
    } catch (error: unknown) {
      Logger.error('Error deleting restore:', error);
      toast.error(getApiErrorMessage(error) || t('management.deleteRestoreError'));
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading && backups.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  return (
    <>
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/deployments/${deploymentId}/manage`)}
              className={`text-sm ${darkModeClasses.textSecondary} hover:${darkModeClasses.textPrimary} mb-4`}
            >
              {`\u2190 ${t('management.backToDeployment')}`}
            </button>
            <h1 className={`text-3xl font-bold ${darkModeClasses.textPrimary}`}>
              {t('management.title')}
              {deploymentName && (
                <span className={`text-xl font-normal ${darkModeClasses.textSecondary} ml-3`}>
                  · {deploymentName}
                </span>
              )}
            </h1>
            <p className={`mt-2 ${darkModeClasses.textSecondary}`}>
              {t('management.subtitle')}
            </p>
          </div>

          {/* Active Restore Banner */}
          {activeRestore && (
            <div className="mb-6 bg-orange-500/12 border border-orange-500/20 rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-orange-500/20">
                    <svg className="h-6 w-6 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-orange-400 mb-1">
                    {t('activeRestore.title')}
                  </h3>
                  <p className="text-sm text-orange-400 mb-3">
                    {activeRestore.status === 'pending' && t('activeRestore.pending')}
                    {activeRestore.status === 'in_progress' && t('activeRestore.inProgress')}
                    {activeRestore.status === 'awaiting_validation' && t('activeRestore.awaitingValidation')}
                  </p>
                  <button
                    onClick={() => navigate(`/deployments/${deploymentId}/restores/${activeRestore.id}`)}
                    className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm font-medium transition-colors"
                  >
                    {t('activeRestore.viewRestore')}
                    <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Configuration Section */}
          <div className={`${darkModeClasses.card} p-6 mb-6`}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-2`}>
                  {t('config.title')}
                </h2>
                {configuration ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className={darkModeClasses.textSecondary}>{t('config.status')}</span>
                      {configuration.enabled ? (
                        <span className="text-green-400 font-medium">✓ {t('config.enabled')}</span>
                      ) : (
                        <span className="text-text-tertiary font-medium">✗ {t('config.disabled')}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={darkModeClasses.textSecondary}>{t('config.schedule')}</span>
                      <span className={`${darkModeClasses.textPrimary} font-mono`}>{configuration.schedule}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={darkModeClasses.textSecondary}>{t('config.retention')}</span>
                      <span className={darkModeClasses.textPrimary}>
                        {t('config.retentionDays', { days: configuration.retention_policy ? String(configuration.retention_policy).replace('d', '') : '0' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={darkModeClasses.textSecondary}>{t('config.s3Path')}</span>
                      <span className={`${darkModeClasses.textPrimary} font-mono text-xs truncate max-w-md`}>
                        {configuration.s3_path}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className={darkModeClasses.textSecondary}>
                    {t('config.noConfig')}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowConfigModal(true)}
                  className="px-4 py-2 bg-surface-3 hover:bg-surface-4 text-white rounded text-sm font-medium transition-colors"
                >
                  {configuration ? t('config.modify') : t('config.configure')}
                </button>
                <button
                  onClick={handleCreateBackup}
                  disabled={creating}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    creating
                      ? 'bg-surface-3 text-text-tertiary cursor-not-allowed'
                      : 'bg-accent-600 hover:bg-accent-700 text-white'
                  }`}
                >
                  {creating ? t('config.creating') : t('config.manualBackup')}
                </button>
              </div>
            </div>
          </div>

          {/* Backups List */}
          <div className={`${darkModeClasses.card} p-6`}>
            <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>
              {t('list.title')}
              {total > 0 && (
                <span className={`ml-2 text-sm font-normal ${darkModeClasses.textSecondary}`}>
                  {t('list.totalCount', { count: total })}
                </span>
              )}
            </h2>

            {backups.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-text-quaternary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                  />
                </svg>
                <h3 className={`mt-2 text-sm font-medium ${darkModeClasses.textPrimary}`}>
                  {t('list.emptyTitle')}
                </h3>
                <p className={`mt-1 text-sm ${darkModeClasses.textSecondary}`}>
                  {t('list.emptySubtitle')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {backups.map((backup) => (
                  <BackupCard
                    key={backup.id}
                    backup={backup}
                    onRestore={handleRestore}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-border-1">
                <div className={`text-sm ${darkModeClasses.textSecondary}`}>
                  {t('list.page', { current: page, total: totalPages })}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-3 py-1 rounded text-sm ${
                      page === 1
                        ? 'bg-surface-3 text-text-quaternary cursor-not-allowed'
                        : 'bg-surface-3 hover:bg-surface-4 text-text-primary'
                    }`}
                  >
                    {t('list.previous')}
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-3 py-1 rounded text-sm ${
                      page === totalPages
                        ? 'bg-surface-3 text-text-quaternary cursor-not-allowed'
                        : 'bg-surface-3 hover:bg-surface-4 text-text-primary'
                    }`}
                  >
                    {t('list.next')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Restore History Section */}
          <div className={`${darkModeClasses.card} p-6 mt-6`}>
            <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>
              {t('restoreHistory.title')}
              {restoreHistory.length > 0 && (
                <span className={`ml-2 text-sm font-normal ${darkModeClasses.textSecondary}`}>
                  ({t('restoreHistory.operations', { count: restoreHistory.length })})
                </span>
              )}
            </h2>

            {restoreHistory.length === 0 ? (
              <div className="text-center py-8">
                <svg
                  className="mx-auto h-12 w-12 text-text-quaternary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className={`mt-2 text-sm font-medium ${darkModeClasses.textPrimary}`}>
                  {t('restoreHistory.emptyTitle')}
                </h3>
                <p className={`mt-1 text-sm ${darkModeClasses.textSecondary}`}>
                  {t('restoreHistory.emptySubtitle')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {restoreHistory.map((restore) => (
                  <RestoreHistoryCard
                    key={restore.id}
                    restore={restore}
                    deploymentId={deploymentId!}
                    onDelete={handleDeleteRestore}
                  />
                ))}
              </div>
            )}
          </div>
      {/* Backup Config Modal */}
      <BackupConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        deploymentId={deploymentId!}
        currentConfig={configuration}
        onConfigured={loadData}
      />
    </>
  );
};

export default BackupManagementPage;
